"use client";

import { useEffect, useState, type DragEvent } from "react";
import {
    Upload,
    Loader2,
    Download,
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
} from "lucide-react";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AxiosError } from "axios";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE } from "@/constants/options";
import type { CardCategory } from "@/services/cardCategoryService";

export interface EventGuestSkippedItem {
    full_name: string;
    reason: string;
}

export interface EventGuestImportResponse {
    message: string;
    imported_count: number;
    updated_count?: number;
    skipped_count: number;
    imported: Record<string, unknown>[];
    updated?: Record<string, unknown>[];
    skipped: EventGuestSkippedItem[];
}

interface EventGuestImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImported: (data: EventGuestImportResponse) => void;
    periodId: number | string;
    periodName?: string;
    categories: CardCategory[];
}

export default function EventGuestImportModal({
    isOpen,
    onClose,
    onImported,
    periodId,
    periodName,
    categories,
}: EventGuestImportModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [skippedData, setSkippedData] = useState<EventGuestSkippedItem[]>([]);
    const [importedData, setImportedData] = useState<Record<string, any>[]>([]);
    const [updatedData, setUpdatedData] = useState<Record<string, any>[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setProgress(0);
            setImporting(false);
            setSkippedData([]);
            setImportedData([]);
            setUpdatedData([]);
        }
    }, [isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFile(e.target.files?.[0] || null);
        setSkippedData([]);
        setImportedData([]);
        setUpdatedData([]);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!importing) setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (importing) return;

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            const droppedFile = droppedFiles[0];
            const fileType = droppedFile.name.split(".").pop()?.toLowerCase();
            if (fileType === "xlsx" || fileType === "csv") {
                setFile(droppedFile);
                setSkippedData([]);
                setImportedData([]);
                setUpdatedData([]);
                toast.success("File berhasil dipilih via Drag & Drop");
            } else {
                toast.error("Format file harus .xlsx atau .csv");
            }
        }
    };

    const handleDownloadTemplate = (): void => {
        window.open("/templates/template_import_event_guests.xlsx", "_blank");
    };

    const handleExportResults = () => {
        // Prepare CSV content
        let csvContent = "Status,Nama Peserta,Alasan / Kategori\n";

        skippedData.forEach((item) => {
            csvContent += `Dilewati/Gagal,"${item.full_name}","${item.reason}"\n`;
        });

        importedData.forEach((item) => {
            csvContent += `Baru,"${item.full_name}","${(item.card_category as CardCategory)?.name || '-'}"\n`;
        });

        updatedData.forEach((item) => {
            csvContent += `Diperbarui,"${item.full_name}","${(item.card_category as CardCategory)?.name || '-'}"\n`;
        });

        // Create Blob and trigger download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Hasil_Import_Panitia_Tamu_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImport = async (): Promise<void> => {
        if (!file) {
            toast.error("Silakan pilih file terlebih dahulu.");
            return;
        }
        if (!periodId) {
            toast.error("Periode belum dipilih.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("period_id", String(periodId));

        try {
            setImporting(true);
            setProgress(0);
            setSkippedData([]);
            setImportedData([]);
            setUpdatedData([]);
            const toastId = toast.loading("Mengimpor data...");

            const { data } = await axiosClient.post<EventGuestImportResponse>(
                "/event-guests/import",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            setProgress(Math.round((evt.loaded * 100) / evt.total));
                        }
                    },
                }
            );

            toast.dismiss(toastId);
            onImported(data);

            const updatedCount = data.updated_count ?? 0;
            const successCount = data.imported_count + updatedCount;

            if (data.skipped_count === 0) {
                toast.success(`Import berhasil sepenuhnya!`);
                setImportedData(data.imported || []);
                setUpdatedData(data.updated || []);
                // setTimeout(() => onClose(), 800); // Do not close automatically so user can see list
            } else if (successCount > 0) {
                toast.success(`Import selesai dengan catatan.`);
                setSkippedData(data.skipped || []);
                setImportedData(data.imported || []);
                setUpdatedData(data.updated || []);
            } else {
                toast.error(`Semua data (${data.skipped_count}) dilewati.`);
                setSkippedData(data.skipped || []);
            }
        } catch (err) {
            toast.dismiss();
            const error = err as AxiosError<{ message?: string; error?: string }>;
            const apiMsg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Gagal mengimpor data.";
            toast.error(apiMsg.length > 120 ? `${apiMsg.substring(0, 120)}...` : apiMsg, { duration: 6000 });
            console.error("Import error:", error.response?.data);
        } finally {
            setImporting(false);
            setProgress(0);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !importing) onClose(); }}>
            <DialogContent onInteractOutside={(e) => e.preventDefault()} className="lg:max-w-5xl w-full bg-white dark:bg-slate-900 border-0 rounded-2xl shadow-xl max-h-[95vh] overflow-y-auto p-6">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                        <Upload className="w-6 h-6 text-blue-600" /> Import Panitia & Tamu
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Periode: <b className="text-indigo-600">{periodName || "-"}</b>
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
                    {/* Info kolom & kategori */}
                    <div className="flex flex-col gap-4">
                        <div className="bg-blue-50 dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 p-4">
                            <h4 className="text-sm font-bold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                <Info className="w-4 h-4" /> Kategori Kartu Tersedia
                            </h4>
                            <p className="text-xs text-slate-500 mt-1 mb-2">
                                Isi kolom <code>card_category</code> persis dengan salah satu nama berikut (untuk periode ini):
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {categories.length === 0 && (
                                    <span className="text-xs text-rose-500">Belum ada kategori kartu untuk periode ini.</span>
                                )}
                                {categories.map((c) => (
                                    <span key={c.id} className="px-2 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[11px] font-medium text-slate-700 dark:text-slate-300">
                                        {c.name}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="bg-blue-50 dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 p-4 space-y-3">
                            <h4 className="text-sm font-bold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                <Info className="w-4 h-4" /> Opsi Kolom Lain
                            </h4>
                            <div>
                                <p className="text-[11px] uppercase tracking-wider font-bold mb-1 text-slate-500">Gender</p>
                                <div className="flex flex-wrap gap-1">
                                    {["male", "female"].map((g) => (
                                        <span key={g} className="px-2 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[11px]">{g}</span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-wider font-bold mb-1 text-slate-500">Golongan Darah</p>
                                <div className="flex flex-wrap gap-1">
                                    {ITEMS_BLOOD_TYPE.map((item) => (
                                        <span key={item.value} className="px-2 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[11px]">{item.value}</span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-wider font-bold mb-1 text-slate-500">Ukuran Baju</p>
                                <div className="flex flex-wrap gap-1">
                                    {ITEMS_CLOTHES_SIZE.map((item) => (
                                        <span key={item.value} className="px-2 py-0.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-[11px]">{item.value}</span>
                                    ))}
                                </div>
                            </div>
                            <p className="text-[10px] text-amber-600 dark:text-amber-400 italic">*Baris kosong pada kolom ini boleh dikosongkan.</p>
                        </div>
                    </div>

                    {/* Template + upload */}
                    <div className="flex flex-col gap-4">
                        <div className="bg-blue-50 dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 p-4 flex items-center justify-between gap-4">
                            <div>
                                <h4 className="text-sm font-bold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                    <Download className="w-4 h-4" /> Template Excel
                                </h4>
                                <p className="text-xs text-slate-500 mt-1">Jangan ubah header kolom.</p>
                            </div>
                            <Button onClick={handleDownloadTemplate} disabled={importing} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                                <Download className="w-4 h-4 mr-2" /> Download
                            </Button>
                        </div>

                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between ${importing
                                ? "border-slate-300 bg-slate-50 dark:bg-slate-800/60 cursor-not-allowed"
                                : isDragging
                                    ? "border-blue-500 bg-blue-100/50 dark:bg-blue-900/40 scale-[1.02] shadow-xl"
                                    : "border-blue-300 dark:border-blue-700 bg-blue-50/30 dark:bg-slate-800/40 hover:bg-blue-50 dark:hover:bg-slate-800/60"
                                }`}
                        >
                            <label
                                htmlFor="event-guest-file-upload"
                                className={`flex flex-col items-center justify-center gap-4 py-16 px-4 text-center cursor-pointer transition-all duration-300 rounded-xl ${file ? "bg-green-50/50 dark:bg-green-900/20 border border-green-200 dark:border-green-800" : ""} ${importing ? "pointer-events-none opacity-70" : ""}`}
                            >
                                {file ? (
                                    <>
                                        <CheckCircle className="w-10 h-10 text-green-600" />
                                        <div>
                                            <p className="text-sm text-green-700 dark:text-green-400 font-bold truncate max-w-[250px]">{file.name}</p>
                                            <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB • Siap diimpor</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                            <Upload className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="text-slate-700 dark:text-slate-300 font-medium">Klik untuk memilih file Excel (.xlsx)</p>
                                            <p className="text-xs text-slate-500 mt-1">Pastikan format sesuai template</p>
                                        </div>
                                    </>
                                )}
                                <input id="event-guest-file-upload" type="file" accept=".xlsx,.csv" onChange={handleFileChange} className="hidden" disabled={importing} />
                            </label>

                            {importing && (
                                <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full mt-4 overflow-hidden">
                                    <div className="bg-blue-600 h-2 transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
                                </div>
                            )}

                            <Button
                                onClick={handleImport}
                                disabled={!file || importing}
                                className={`mt-4 w-full py-2 text-white transition-all duration-300 shadow-sm ${importing ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 hover:shadow-md"}`}
                            >
                                {importing ? (
                                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Memproses {progress}%...</>
                                ) : (
                                    <><Upload className="w-4 h-4 mr-2" /> Mulai Import</>
                                )}
                            </Button>
                        </div>

                        {skippedData.length > 0 && (
                            <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                                <div className="flex items-center gap-2 mb-3 text-red-700 dark:text-red-400">
                                    <AlertCircle className="w-5 h-5" />
                                    <h5 className="font-bold text-sm">{skippedData.length} Data Dilewati</h5>
                                </div>
                                <div className="max-h-[150px] overflow-y-auto pr-2">
                                    <ul className="space-y-2">
                                        {skippedData.map((item, idx) => (
                                            <li key={idx} className="text-xs bg-white dark:bg-slate-800 p-2.5 rounded-lg border border-red-100 dark:border-red-900/50 shadow-sm flex items-start gap-2">
                                                <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                <div>
                                                    <span className="font-bold text-slate-800 dark:text-slate-200 block mb-0.5">{item.full_name}</span>
                                                    <span className="text-red-600 dark:text-red-400">{item.reason}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {importedData.length > 0 && (
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-4">
                                <div className="flex items-center gap-2 mb-3 text-emerald-700 dark:text-emerald-400">
                                    <CheckCircle className="w-5 h-5" />
                                    <h5 className="font-bold text-sm">{importedData.length} Data Berhasil Diimport Baru</h5>
                                </div>
                                <div className="max-h-[120px] overflow-y-auto pr-2">
                                    <ul className="space-y-2">
                                        {importedData.map((item, idx) => (
                                            <li key={idx} className="text-xs bg-white dark:bg-slate-800 p-2 rounded border border-emerald-100 dark:border-emerald-900/50 shadow-sm">
                                                <span className="font-semibold text-slate-800 dark:text-slate-200">{item.full_name as string}</span>
                                                <span className="text-slate-500 block">Kategori: {item.card_category?.name || '-'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {updatedData.length > 0 && (
                            <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-4">
                                <div className="flex items-center gap-2 mb-3 text-blue-700 dark:text-blue-400">
                                    <Info className="w-5 h-5" />
                                    <h5 className="font-bold text-sm">{updatedData.length} Data Berhasil Diupdate</h5>
                                </div>
                                <div className="max-h-[120px] overflow-y-auto pr-2">
                                    <ul className="space-y-2">
                                        {updatedData.map((item, idx) => (
                                            <li key={idx} className="text-xs bg-white dark:bg-slate-800 p-2 rounded border border-blue-100 dark:border-blue-900/50 shadow-sm">
                                                <span className="font-semibold text-slate-800 dark:text-slate-200">{item.full_name as string}</span>
                                                <span className="text-slate-500 block">Kategori: {item.card_category?.name || '-'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {(skippedData.length > 0 || importedData.length > 0 || updatedData.length > 0) && (
                            <div className="mt-2 flex justify-end">
                                <Button
                                    variant="outline"
                                    onClick={handleExportResults}
                                    className="w-full sm:w-auto bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm"
                                >
                                    <Download className="w-4 h-4 mr-2" /> Download Laporan Hasil Import (.csv)
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-6 flex justify-end">
                    <Button variant="ghost" onClick={onClose} disabled={importing}>Tutup</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
